<template>
  <div class="photo-slot">
    <div class="photo-label">{{ type === 'luggage' ? 'Bagage Foto' : 'Stregkode Foto' }}</div>
    <div class="photo-container">
      <!-- Upload zone with inline loader -->
      <div v-if="!displayPhotoUrl" class="upload-zone" :class="{ 'is-uploading': uploading }">
        <div v-if="!uploading" class="upload-options">
          <span class="upload-title">Tilføj {{ type === 'luggage' ? 'Foto' : 'Stregkode' }}</span>
          <div class="upload-actions">
            <button type="button" class="upload-action upload-action-camera" @click="triggerCamera">
              Tag foto
            </button>
            <button type="button" class="upload-action" @click="triggerGallery">
              Vælg fra galleri
            </button>
          </div>
          <span class="upload-help">Vent til upload er færdig</span>
        </div>
        <div v-if="uploading" class="inline-loader" role="status" aria-live="polite">
          <div class="spinner"></div>
          <div class="loading-text">{{ uploadStatusText }}</div>
          <div class="progress-track" :class="{ 'is-indeterminate': uploadProgress === null }">
            <div class="progress-fill" :style="progressStyle"></div>
          </div>
          <div class="progress-text">{{ uploadProgressText }}</div>
          <div class="wait-text">Bliv på siden. Det kan tage lidt tid på mobilt internet.</div>
        </div>
      </div>
      
      <!-- Photo preview with overlay loader -->
      <div v-if="displayPhotoUrl" class="photo-preview">
        <img 
          :src="displayPhotoUrl"
          alt="Photo" 
          @click="viewPhoto($event)"
          class="photo-image"
        />
        <button v-if="photoUrl && !uploading" @click="deletePhoto($event)" class="delete-btn">×</button>
        
        <!-- Loading overlay for upload and deletion -->
        <div v-if="uploading" class="loading-overlay">
          <div class="spinner"></div>
          <div class="loading-text">{{ isDeleting ? 'Sletter foto...' : uploadStatusText }}</div>
        </div>
      </div>
    </div>
    <input
      ref="cameraInput"
      type="file"
      accept="image/*"
      capture="environment"
      style="display:none"
      @change="onFileChange"
    />
    <input
      ref="galleryInput"
      type="file"
      accept="image/*,.heic,.heif"
      style="display:none"
      @change="onFileChange"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { event as trackAnalyticsEvent } from 'vue-analytics'
import {
  MAX_SOURCE_IMAGE_BYTES,
  MAX_UPLOAD_IMAGE_BYTES,
  optimizeImageForUpload
} from '../utils/imageOptimizer'

export default {
  name: 'PhotoUploadSlot',
  props: {
    personIndex: {
      type: Number,
      required: true
    },
    luggageType: {
      type: String,
      required: true
    },
    itemIndex: {
      type: Number,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    photos: {
      type: Object,
      required: true
    },
    orderToken: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      uploading: false,
      isDeleting: false,
      optimizing: false,
      uploadProgress: null,
      uploadProcessing: false,
      localPreviewUrl: ''
    }
  },
  computed: {
    photoUrl() {
      const key = `${this.personIndex}_${this.luggageType}_${this.itemIndex}_${this.type}`
      return (this.photos && this.photos[key]) ? this.photos[key] : null
    },

    displayPhotoUrl() {
      return this.localPreviewUrl || this.photoUrl
    },

    uploadStatusText() {
      if (this.optimizing) {
        return 'Optimerer foto...'
      }

      if (this.uploadProcessing) {
        return 'Gemmer foto...'
      }

      return 'Uploader foto...'
    },

    uploadProgressText() {
      if (this.optimizing) {
        return 'Forbereder foto'
      }

      if (this.uploadProgress === null) {
        return 'Upload er startet'
      }

      return `${this.uploadProgress}% uploadet`
    },

    progressStyle() {
      if (this.uploadProgress === null) {
        return {}
      }

      return {
        width: `${this.uploadProgress}%`
      }
    }
  },
  beforeDestroy() {
    this.cancelRemotePreviewPreload()
    this.clearLocalPreview()
  },
  methods: {
    clearLocalPreview() {
      if (!this.localPreviewUrl) {
        return
      }

      window.URL.revokeObjectURL(this.localPreviewUrl)
      this.localPreviewUrl = ''
    },

    setLocalPreview(file) {
      this.cancelRemotePreviewPreload()
      this.clearLocalPreview()
      this.localPreviewUrl = window.URL.createObjectURL(file)
    },

    cancelRemotePreviewPreload() {
      if (!this._remotePreviewImage) {
        return
      }

      this._remotePreviewImage.onload = null
      this._remotePreviewImage.onerror = null
      this._remotePreviewImage = null
    },

    preloadRemotePreview(remoteUrl) {
      if (!remoteUrl || !this.localPreviewUrl) {
        return
      }

      this.cancelRemotePreviewPreload()

      const localPreviewUrl = this.localPreviewUrl
      const remoteImage = new window.Image()

      remoteImage.onload = () => {
        if (this.localPreviewUrl === localPreviewUrl) {
          this.clearLocalPreview()
        }
        this._remotePreviewImage = null
      }

      remoteImage.onerror = () => {
        // Keep the local preview if the remote image is slow or temporarily unavailable.
        this._remotePreviewImage = null
      }

      this._remotePreviewImage = remoteImage
      remoteImage.src = remoteUrl
    },

    reportOptimizationFallback(optimizationResult, fileExtension) {
      try {
        trackAnalyticsEvent(
          'Luggage photo upload',
          'Optimization fallback',
          `${optimizationResult.fallbackReason}:${this.type}:${fileExtension}`
        )
      } catch (error) {
        // Analytics must never prevent the customer from uploading a photo.
      }
    },

    triggerCamera() {
      this.$refs.cameraInput.click()
    },

    triggerGallery() {
      this.$refs.galleryInput.click()
    },

    viewPhoto(event) {
      event.stopPropagation()
      event.preventDefault()
      
      this.$emit('view-photo', {
        url: this.photoUrl || this.displayPhotoUrl,
        title: `${this.type === 'luggage' ? 'Bagage Foto' : 'Stregkode Foto'} - Genstand ${this.itemIndex + 1}`
      })
    },

    async onFileChange(e) {
      const fileInput = e.target
      const file = e.target.files[0]
      if (!file) return

      const fileExtension = file.name.split('.').pop().toLowerCase()
      const allowedImageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif']

      if (!file.type.startsWith('image/') && !allowedImageExtensions.includes(fileExtension)) {
        fileInput.value = ''
        this.$emit('show-message', { type: 'error', message: 'Kun billedfiler tilladt' })
        return
      }

      if (file.size > MAX_SOURCE_IMAGE_BYTES) {
        fileInput.value = ''
        this.$emit('show-message', { type: 'error', message: 'Fil for stor til behandling (maks 25MB)' })
        return
      }
      
      this.isDeleting = false
      this.uploading = true
      this.optimizing = true
      this.uploadProgress = null
      this.uploadProcessing = false

      try {
        const optimizationResult = await optimizeImageForUpload(file, this.type)
        const uploadFile = optimizationResult.file

        if (optimizationResult.shouldReportFallback) {
          this.reportOptimizationFallback(optimizationResult, fileExtension)
        }

        this.optimizing = false

        if (uploadFile.size > MAX_UPLOAD_IMAGE_BYTES) {
          this.$emit('show-message', {
            type: 'error',
            message: 'Filen er stadig for stor efter optimering (maks 10MB)'
          })
          return
        }

        const unsafePreviewFallbacks = [
          'pixel_limit',
          'large_image_resize_unsupported',
          'large_image_decode_failed',
          'large_image_resize_failed'
        ]
        const canPreviewLocally = optimizationResult.optimized ||
          (
            !['heic', 'heif'].includes(fileExtension) &&
            !unsafePreviewFallbacks.includes(optimizationResult.fallbackReason)
          )

        if (canPreviewLocally) {
          this.setLocalPreview(uploadFile)
        }

        this.uploadProgress = 0

        const formData = new FormData()
        formData.append('photo', uploadFile)
        formData.append('order_token', this.orderToken)
        formData.append('person_index', this.personIndex)
        formData.append('luggage_type', this.luggageType)
        formData.append('item_index', this.itemIndex)
        formData.append('photo_type', this.type)

        const response = await axios.post(`${process.env.VUE_APP_API_URL}/upload-luggage-photo`, formData, {
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.lengthComputable || !progressEvent.total) {
              this.uploadProgress = null
              return
            }

            const percent = Math.min(99, Math.round((progressEvent.loaded * 100) / progressEvent.total))
            this.uploadProgress = percent

            if (percent >= 99) {
              this.uploadProcessing = true
            }
          }
        })
        
        if (response.data.success && response.data.data && response.data.data.photo_url) {
          this.uploadProgress = 100
          this.uploadProcessing = false

          const uploadData = {
            personIndex: this.personIndex,
            luggageType: this.luggageType,
            itemIndex: this.itemIndex,
            photoType: this.type,
            photoUrl: response.data.data.photo_url
          }
          
          this.$emit('photoUploaded', uploadData)
          
          // Fallback: direct parent call
          if (this.$parent && this.$parent.photoUploaded) {
            this.$parent.photoUploaded(uploadData)
          }
          
          this.$emit('photo-uploaded', uploadData)
          this.$emit('show-message', { type: 'success', message: 'Foto uploadet med succes!' })
          this.$nextTick(() => this.preloadRemotePreview(uploadData.photoUrl))
        } else {
          this.clearLocalPreview()
          const message = response.data && response.data.data && response.data.data.message
            ? response.data.data.message
            : 'Upload fejlede'
          this.$emit('show-message', { type: 'error', message })
        }
      } catch (error) {
        this.clearLocalPreview()
        this.$emit('show-message', { type: 'error', message: 'Upload fejl: ' + error.message })
      } finally {
        this.uploading = false
        this.optimizing = false
        this.uploadProgress = null
        this.uploadProcessing = false
        fileInput.value = ''
      }
    },

    async deletePhoto(event) {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      
      if (!confirm('Slet dette foto?')) return
      
      this.isDeleting = true
      this.uploading = true
      this.optimizing = false
      this.uploadProgress = null
      this.uploadProcessing = false
      
      const deletePayload = {
        order_token: this.orderToken,
        person_index: this.personIndex,
        luggage_type: this.luggageType,
        item_index: this.itemIndex,
        photo_type: this.type
      }
      
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/remove-luggage-photo`, deletePayload)
        
        if (response.data.success) {
          this.cancelRemotePreviewPreload()
          this.clearLocalPreview()

          const deleteData = {
            personIndex: this.personIndex,
            luggageType: this.luggageType,
            itemIndex: this.itemIndex,
            photoType: this.type
          }
          
          this.$emit('photoDeleted', deleteData)
          this.$emit('show-message', { type: 'success', message: 'Foto slettet med succes!' })
        } else {
          this.$emit('show-message', { type: 'error', message: 'Sletning fejlede' })
        }
      } catch (error) {
        this.$emit('show-message', { type: 'error', message: 'Sletnings fejl: ' + error.message })
      } finally {
        this.uploading = false
        this.isDeleting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.photo-slot {
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.photo-container {
  position: relative;
}

.photo-label {
  font-size: 12px;
  font-weight: 600;
  color: #00A9E0;
  margin-bottom: 5px;
}

.upload-zone {
  border: 2px dashed #E2E2E2;
  border-radius: 6px;
  padding: 14px;
  text-align: center;
  transition: all 0.3s ease;
  min-height: 136px;
  height: 136px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(.is-uploading) {
    border-color: #00A9E0;
    background: #F8FBFF;
  }

  &.is-uploading {
    cursor: wait;
    border-color: #00A9E0;
    background: #F8FBFF;
  }
  
  span {
    display: block;
  }

  .upload-title {
    color: #333;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .upload-help {
    color: #666;
    font-size: 12px;
    line-height: 1.35;
    margin-top: 7px;
  }
}

.upload-options {
  width: 100%;
}

.upload-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.upload-action {
  flex: 1 1 0;
  min-height: 34px;
  padding: 7px 9px;
  border: 1px solid #00A9E0;
  border-radius: 5px;
  background: white;
  color: #007FA8;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    background: #EAF8FD;
    outline: none;
  }

  &.upload-action-camera {
    background: #00A9E0;
    color: white;

    &:hover,
    &:focus {
      background: #008FBD;
    }
  }
}

.inline-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  .spinner {
    width: 26px;
    height: 26px;
    border: 3px solid #E2E2E2;
    border-top: 3px solid #00A9E0;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 8px;
  }
  
  .loading-text {
    color: #00A9E0;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 7px;
  }

  .progress-track {
    width: 100%;
    height: 8px;
    background: #E2E2E2;
    border-radius: 999px;
    overflow: hidden;
    position: relative;
    margin-bottom: 5px;
  }

  .progress-fill {
    height: 100%;
    width: 0;
    background: #00A9E0;
    border-radius: inherit;
    transition: width 0.2s ease;
  }

  .progress-track.is-indeterminate {
    .progress-fill {
      position: absolute;
      width: 45%;
      animation: progress-slide 1.2s ease-in-out infinite;
    }
  }

  .progress-text {
    color: #333;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 4px;
  }

  .wait-text {
    color: #666;
    font-size: 11px;
    line-height: 1.25;
    max-width: 220px;
  }
}

.photo-preview {
  position: relative;
  height: 136px;
  
  .photo-image {
    width: 100%;
    height: 136px;
    object-fit: cover;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
  
  .delete-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    color: #ff4444;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: white;
      color: #ff0000;
    }
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.72);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  z-index: 1000;
  backdrop-filter: blur(1px);
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #E2E2E2;
    border-top: 3px solid #00A9E0;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 10px;
  }
  
  .loading-text {
    color: #00A9E0;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes progress-slide {
  0% { left: -45%; }
  100% { left: 100%; }
}
</style>
